import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Calendar, Search, Loader2, RefreshCw, Clock,
  CheckCircle, XCircle, AlertTriangle, Trash2, Edit3,
  Instagram, X, Film, MoreVertical, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useAuth } from '../../components/auth/AuthContext';
import { socialConnectionsService } from '../../services/SocialConnectionsService';

// Ícone do TikTok
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// ============================================
// MODAL DE EDIÇÃO
// ============================================
const EditScheduleModal = ({ post, onClose, onSave }) => {
  const [caption, setCaption] = useState(post?.caption || '');
  const [scheduledAt, setScheduledAt] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (post?.scheduled_at) {
      const date = new Date(post.scheduled_at);
      const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      setScheduledAt(localDate.toISOString().slice(0, 16));
    }
  }, [post]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(post.id, {
        caption: caption.trim() || undefined,
        scheduledAt: new Date(scheduledAt).toISOString()
      });
      onClose();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar alterações. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!post) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-gray-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">Editar Agendamento</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex gap-4">
            <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
              {post.content_snapshot?.type === 'video' ? (
                <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Film className="w-8 h-8 text-white" />
                </div>
              ) : (
                <img src={post.content_snapshot?.result_url} alt="Preview" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {post.platform === 'instagram' ? <Instagram className="w-4 h-4 text-pink-500" /> : <TikTokIcon className="w-4 h-4 text-gray-900" />}
                <span className="text-sm font-medium text-gray-700 capitalize">{post.platform}</span>
              </div>
              <p className="text-xs text-gray-500">{post.content_snapshot?.type === 'video' ? 'Vídeo' : 'Imagem'}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Data e Hora</label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              min={new Date().toISOString().slice(0, 16)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Legenda</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Adicione uma legenda..."
              rows={4}
              maxLength={2200}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400 transition-all resize-none"
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{caption.length}/2200</p>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all">
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !scheduledAt}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-500 hover:to-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSaving ? <><Loader2 className="w-4 h-4 animate-spin" />Salvando...</> : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MODAL DE DETALHES DO DIA
// ============================================
const DayDetailModal = ({ date, posts, onClose, onEdit, onCancel, onDelete }) => {
  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Agendado', color: 'bg-amber-100 text-amber-700' },
      processing: { label: 'Publicando', color: 'bg-blue-100 text-blue-700' },
      published: { label: 'Publicado', color: 'bg-green-100 text-green-700' },
      failed: { label: 'Falhou', color: 'bg-red-100 text-red-700' },
      cancelled: { label: 'Cancelado', color: 'bg-gray-100 text-gray-600' }
    };
    return configs[status] || configs.pending;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-gray-200" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900">
            {date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4 max-h-[60vh] overflow-y-auto space-y-3">
          {posts.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Nenhum post agendado para este dia.</p>
          ) : (
            posts.map(post => {
              const statusConfig = getStatusConfig(post.status);
              const canEdit = post.status === 'pending';
              
              return (
                <div key={post.id} className="bg-gray-50 rounded-xl p-4 flex gap-3">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                    {post.content_snapshot?.type === 'video' ? (
                      <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                        <Film className="w-5 h-5 text-white" />
                      </div>
                    ) : (
                      <img src={post.content_snapshot?.result_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900">{formatTime(post.scheduled_at)}</span>
                      {post.platform === 'instagram' ? <Instagram className="w-3.5 h-3.5 text-pink-500" /> : <TikTokIcon className="w-3.5 h-3.5 text-gray-900" />}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
                    </div>
                    <p className="text-xs text-gray-600 line-clamp-2">{post.caption || 'Sem legenda'}</p>
                    
                    {canEdit && (
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => { onEdit(post); onClose(); }} className="text-xs text-amber-600 hover:text-amber-700 font-medium">Editar</button>
                        <button onClick={() => { onCancel(post); onClose(); }} className="text-xs text-gray-500 hover:text-gray-700 font-medium">Cancelar</button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// COMPONENTE DE CALENDÁRIO
// ============================================
const CalendarView = ({ posts, onEdit, onCancel, onDelete }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [calendarMode, setCalendarMode] = useState('month'); // 'month' ou 'week'

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const weekDaysFull = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

  // Gerar dias do mês
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    
    // Dias vazios no início
    for (let i = 0; i < startPadding; i++) {
      days.push({ day: null, date: null });
    }
    
    // Dias do mês
    for (let i = 1; i <= totalDays; i++) {
      const date = new Date(year, month, i);
      days.push({ day: i, date });
    }
    
    return days;
  }, [currentDate]);

  // Gerar dias da semana
  const weekDaysData = useMemo(() => {
    const startOfWeek = new Date(currentDate);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      days.push({ 
        day: date.getDate(), 
        date,
        dayName: weekDaysFull[i]
      });
    }
    
    return days;
  }, [currentDate]);

  // Agrupar posts por dia
  const postsByDay = useMemo(() => {
    const grouped = {};
    posts.forEach(post => {
      const date = new Date(post.scheduled_at);
      const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(post);
    });
    return grouped;
  }, [posts]);

  const getPostsForDay = (date) => {
    if (!date) return [];
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return postsByDay[key] || [];
  };

  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToPrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() && 
           date.getFullYear() === today.getFullYear();
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const getPostColor = (post) => {
    if (post.status === 'published') return 'bg-green-500';
    if (post.status === 'failed') return 'bg-red-500';
    if (post.status === 'cancelled') return 'bg-gray-400';
    // Por plataforma para pending
    return post.platform === 'instagram' ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-gray-800';
  };

  // Calcular range da semana para exibição
  const getWeekRange = () => {
    const start = weekDaysData[0].date;
    const end = weekDaysData[6].date;
    
    const startStr = start.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' });
    const endStr = end.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' });
    
    return `${startStr} - ${endStr}`;
  };

  return (
    <>
      {/* Modal de detalhes do dia */}
      {selectedDay && (
        <DayDetailModal
          date={selectedDay.date}
          posts={selectedDay.posts}
          onClose={() => setSelectedDay(null)}
          onEdit={onEdit}
          onCancel={onCancel}
          onDelete={onDelete}
        />
      )}

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {/* Header do calendário */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2 sm:gap-4">
            <button 
              onClick={calendarMode === 'month' ? goToPrevMonth : goToPrevWeek} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h3 className="text-base sm:text-lg font-bold text-gray-900 min-w-[160px] sm:min-w-[200px] text-center">
              {calendarMode === 'month' 
                ? currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
                : getWeekRange()
              }
            </h3>
            <button 
              onClick={calendarMode === 'month' ? goToNextMonth : goToNextWeek} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Toggle Mês/Semana */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setCalendarMode('month')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  calendarMode === 'month' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Mês
              </button>
              <button
                onClick={() => setCalendarMode('week')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  calendarMode === 'week' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Semana
              </button>
            </div>
            
            <button 
              onClick={goToToday}
              className="px-3 sm:px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Hoje
            </button>
          </div>
        </div>

        {/* ========== VISÃO MÊS ========== */}
        {calendarMode === 'month' && (
          <>
            {/* Dias da semana */}
            <div className="grid grid-cols-7 border-b border-gray-100">
              {weekDays.map(day => (
                <div key={day} className="px-2 py-3 text-center text-sm font-medium text-gray-500">
                  {day}
                </div>
              ))}
            </div>

            {/* Grid de dias */}
            <div className="grid grid-cols-7">
              {calendarDays.map((item, idx) => {
                const dayPosts = getPostsForDay(item.date);
                const hasMore = dayPosts.length > 2;
                
                return (
                  <div
                    key={idx}
                    className={`min-h-[120px] border-b border-r border-gray-100 p-2 ${
                      item.day ? 'hover:bg-gray-50 cursor-pointer' : 'bg-gray-50/50'
                    } ${idx % 7 === 6 ? 'border-r-0' : ''}`}
                    onClick={() => item.day && dayPosts.length > 0 && setSelectedDay({ date: item.date, posts: dayPosts })}
                  >
                    {item.day && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${
                          isToday(item.date) 
                            ? 'w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center' 
                            : 'text-gray-700 pl-1'
                        }`}>
                          {item.day}
                        </div>
                        
                        <div className="space-y-1">
                          {dayPosts.slice(0, 2).map(post => (
                            <div
                              key={post.id}
                              className={`${getPostColor(post)} text-white text-xs px-2 py-1 rounded truncate`}
                              title={post.caption || 'Sem legenda'}
                            >
                              {formatTime(post.scheduled_at)} {post.caption?.slice(0, 15) || (post.platform === 'instagram' ? 'Instagram' : 'TikTok')}
                            </div>
                          ))}
                          
                          {hasMore && (
                            <div className="text-xs text-gray-500 pl-1 font-medium">
                              +{dayPosts.length - 2} mais
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ========== VISÃO SEMANA ========== */}
        {calendarMode === 'week' && (
          <>
            {/* Header com dias da semana */}
            <div className="grid grid-cols-7 border-b border-gray-100">
              {weekDaysData.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`px-2 py-3 text-center border-r border-gray-100 last:border-r-0 ${
                    isToday(item.date) ? 'bg-amber-50' : ''
                  }`}
                >
                  <p className="text-xs font-medium text-gray-500 uppercase">
                    {weekDays[idx]}
                  </p>
                  <p className={`text-lg font-bold mt-1 ${
                    isToday(item.date) 
                      ? 'w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center mx-auto' 
                      : 'text-gray-900'
                  }`}>
                    {item.day}
                  </p>
                </div>
              ))}
            </div>

            {/* Grid de conteúdos da semana */}
            <div className="grid grid-cols-7 min-h-[400px]">
              {weekDaysData.map((item, idx) => {
                const dayPosts = getPostsForDay(item.date);
                
                return (
                  <div
                    key={idx}
                    className={`border-r border-gray-100 last:border-r-0 p-2 ${
                      isToday(item.date) ? 'bg-amber-50/30' : ''
                    } ${dayPosts.length > 0 ? 'hover:bg-gray-50 cursor-pointer' : ''}`}
                    onClick={() => dayPosts.length > 0 && setSelectedDay({ date: item.date, posts: dayPosts })}
                  >
                    <div className="space-y-2">
                      {dayPosts.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center pt-4">-</p>
                      ) : (
                        dayPosts.map(post => (
                          <div
                            key={post.id}
                            className={`${getPostColor(post)} text-white text-xs p-2 rounded-lg`}
                            title={post.caption || 'Sem legenda'}
                          >
                            <div className="flex items-center gap-1 mb-1">
                              {post.platform === 'instagram' 
                                ? <Instagram className="w-3 h-3" /> 
                                : <TikTokIcon className="w-3 h-3" />
                              }
                              <span className="font-semibold">{formatTime(post.scheduled_at)}</span>
                            </div>
                            <p className="line-clamp-2 opacity-90">
                              {post.caption?.slice(0, 40) || (post.content_snapshot?.type === 'video' ? 'Vídeo' : 'Imagem')}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
};

// ============================================
// COMPONENTE DE LISTA
// ============================================
const ListView = ({ posts, onEdit, onCancel, onDelete, searchTerm }) => {
  const [showMenu, setShowMenu] = useState(null);

  const getStatusConfig = (status) => {
    const configs = {
      pending: { label: 'Agendado', color: 'bg-amber-100 text-amber-700', icon: Clock },
      processing: { label: 'Publicando', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
      published: { label: 'Publicado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      failed: { label: 'Falhou', color: 'bg-red-100 text-red-700', icon: XCircle },
      cancelled: { label: 'Cancelado', color: 'bg-gray-100 text-gray-600', icon: X }
    };
    return configs[status] || configs.pending;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const filteredPosts = posts.filter(post => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return post.caption?.toLowerCase().includes(term) || post.platform?.toLowerCase().includes(term);
  });

  if (filteredPosts.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl flex items-center justify-center">
          <Calendar className="w-10 h-10 text-amber-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {searchTerm ? 'Nenhum post encontrado' : 'Nenhum post agendado'}
        </h3>
        <p className="text-gray-500 max-w-md mx-auto">
          {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Quando você agendar posts, eles aparecerão aqui.'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {filteredPosts.map(post => {
        const statusConfig = getStatusConfig(post.status);
        const isPast = new Date(post.scheduled_at) < new Date();
        const canEdit = post.status === 'pending' && !isPast;

        return (
          <div key={post.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                {post.content_snapshot?.type === 'video' ? (
                  <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <Film className="w-6 h-6 text-white" />
                  </div>
                ) : (
                  <img src={post.content_snapshot?.result_url} alt="Preview" className="w-full h-full object-cover" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {post.platform === 'instagram' ? <Instagram className="w-4 h-4 text-pink-500" /> : <TikTokIcon className="w-4 h-4 text-gray-900" />}
                  <span className="text-sm font-medium text-gray-700 capitalize">{post.platform}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
                </div>
                <p className="text-sm text-gray-600 truncate">{post.caption || 'Sem legenda'}</p>
              </div>

              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold text-gray-900">{formatDate(post.scheduled_at)}</p>
                <p className="text-sm text-gray-500">{formatTime(post.scheduled_at)}</p>
              </div>

              <div className="relative flex-shrink-0">
                <button onClick={() => setShowMenu(showMenu === post.id ? null : post.id)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <MoreVertical className="w-5 h-5 text-gray-400" />
                </button>

                {showMenu === post.id && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowMenu(null)} />
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 py-1 min-w-[140px] z-20">
                      {canEdit && (
                        <>
                          <button onClick={() => { onEdit(post); setShowMenu(null); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <Edit3 className="w-4 h-4" /> Editar
                          </button>
                          <button onClick={() => { onCancel(post); setShowMenu(null); }} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                            <X className="w-4 h-4" /> Cancelar
                          </button>
                        </>
                      )}
                      <button onClick={() => { onDelete(post); setShowMenu(null); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                        <Trash2 className="w-4 h-4" /> Excluir
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// PÁGINA PRINCIPAL
// ============================================
const ScheduledPostsPage = () => {
  const { currentUser } = useAuth();
  const firstName = currentUser?.name?.split(' ')[0] || 'Usuário';

  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [viewMode, setViewMode] = useState('calendar');

  const [editingPost, setEditingPost] = useState(null);

  const fetchPosts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const options = { includePast: true, limit: 100 };
      if (statusFilter !== 'all') options.status = statusFilter;
      if (platformFilter !== 'all') options.platform = platformFilter;

      const response = await socialConnectionsService.getScheduledPosts(options);
      setPosts(response.scheduled_posts || []);
    } catch (err) {
      console.error('Erro ao buscar posts:', err);
      setError(err.response?.data?.detail || err.message || 'Não foi possível carregar os agendamentos.');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, platformFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await socialConnectionsService.getCalendarStats();
      setStats(response.stats);
    } catch (err) {
      console.error('Erro ao buscar stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchStats();
  }, [fetchPosts, fetchStats]);

  const handleEdit = (post) => setEditingPost(post);

  const handleSaveEdit = async (postId, updates) => {
    await socialConnectionsService.updateScheduledPost(postId, updates);
    fetchPosts();
    fetchStats();
  };

  const handleCancel = async (post) => {
    if (!window.confirm('Deseja cancelar este agendamento?')) return;
    try {
      await socialConnectionsService.cancelScheduledPost(post.id, false);
      fetchPosts();
      fetchStats();
    } catch (err) {
      alert('Erro ao cancelar agendamento.');
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm('Deseja excluir permanentemente este agendamento?')) return;
    try {
      await socialConnectionsService.cancelScheduledPost(post.id, true);
      fetchPosts();
      fetchStats();
    } catch (err) {
      alert('Erro ao excluir agendamento.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {editingPost && (
        <EditScheduleModal post={editingPost} onClose={() => setEditingPost(null)} onSave={handleSaveEdit} />
      )}

      <header className="sticky top-0 z-20 pt-4 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white/60 backdrop-blur-md rounded-3xl border border-gray-200/50 px-6 shadow-sm">
            <div className="flex items-center justify-between h-20">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900">Agendamentos</h1>
                </div>
              </div>
              
              <button
                onClick={() => { fetchPosts(); fetchStats(); }}
                disabled={isLoading}
                className="p-2.5 hover:bg-white rounded-xl transition-colors border border-transparent hover:border-gray-200"
                title="Atualizar"
              >
                <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <section className="mb-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center mb-2">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_status?.pending || 0}</p>
                <p className="text-sm text-gray-500">Agendados</p>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_status?.published || 0}</p>
                <p className="text-sm text-gray-500">Publicados</p>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="w-10 h-10 bg-gradient-to-br from-pink-100 to-rose-100 rounded-xl flex items-center justify-center mb-2">
                  <Instagram className="w-5 h-5 text-pink-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_platform?.instagram || 0}</p>
                <p className="text-sm text-gray-500">Instagram</p>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center mb-2">
                  <TikTokIcon className="w-5 h-5 text-gray-700" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{stats.by_platform?.tiktok || 0}</p>
                <p className="text-sm text-gray-500">TikTok</p>
              </div>
            </div>
          </section>
        )}

        {/* Filtros e Toggle de View */}
        <section className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              {viewMode === 'list' && (
                <div className="relative flex-1 sm:flex-initial">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full sm:w-48 pl-10 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400 transition-all"
                  />
                </div>
              )}

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400 transition-all cursor-pointer"
              >
                <option value="all">Todos status</option>
                <option value="pending">Agendados</option>
                <option value="published">Publicados</option>
                <option value="failed">Falharam</option>
                <option value="cancelled">Cancelados</option>
              </select>

              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="px-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-100 focus:border-amber-400 transition-all cursor-pointer"
              >
                <option value="all">Todas plataformas</option>
                <option value="instagram">Instagram</option>
                <option value="tiktok">TikTok</option>
              </select>
            </div>

            {/* View Toggle */}
            <div className="flex items-center bg-white border border-gray-200 rounded-xl p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'calendar' ? 'bg-amber-100 text-amber-700' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Calendário
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'list' ? 'bg-amber-100 text-amber-700' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Lista
              </button>
            </div>
          </div>
        </section>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center mb-4">
              <Loader2 className="w-6 h-6 text-amber-600 animate-spin" />
            </div>
            <p className="text-gray-500 font-medium">Carregando agendamentos...</p>
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <p className="text-red-700 font-medium mb-2">Erro ao carregar</p>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button onClick={fetchPosts} className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-all">
              Tentar novamente
            </button>
          </div>
        )}

        {/* Content */}
        {!isLoading && !error && (
          viewMode === 'calendar' ? (
            <CalendarView posts={posts} onEdit={handleEdit} onCancel={handleCancel} onDelete={handleDelete} />
          ) : (
            <ListView posts={posts} onEdit={handleEdit} onCancel={handleCancel} onDelete={handleDelete} searchTerm={searchTerm} />
          )
        )}
      </div>
    </div>
  );
};

export default ScheduledPostsPage;